import spriteUrl from './sprite@2x.png';
import spriteIndex from './sprite@2x.json';

export interface SpriteCell {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio: number;
}

interface MapWithImages {
  hasImage: (name: string) => boolean;
  addImage: (
    name: string,
    image: ImageBitmap,
    options?: { pixelRatio?: number },
  ) => void;
}

const SPRITE_CELLS: { [name: string]: SpriteCell } = spriteIndex;

/** The sprite sheet, for drawing a boat outside the map (e.g. a CSS background). */
export const BOAT_SPRITE_URL: string = spriteUrl;

export const boatSpriteCell = (name: string): SpriteCell | undefined =>
  SPRITE_CELLS[name];

/** Sheet size in sprite pixels, from the cell layout. */
export const BOAT_SPRITE_SHEET_SIZE = Object.keys(SPRITE_CELLS).reduce(
  (size, name) => {
    const { x, y, width, height } = SPRITE_CELLS[name];
    return {
      width: Math.max(size.width, x + width),
      height: Math.max(size.height, y + height),
    };
  },
  { width: 0, height: 0 },
);

/**
 * Top views are registered at startup. Tilted frames load on demand when the
 * map is pitched.
 */
export const BOAT_TOP_ICON_NAMES = Object.keys(SPRITE_CELLS).filter((name) =>
  /^boat-[a-z]+-top$/.test(name),
);

const TILTED_FRAME_COUNT = 16;

/** The 16 tilted frame names (`-00` to `-15`) for a top view name. */
export const tiltedBoatIconNames = (topIconName: string): string[] =>
  Array.from({ length: TILTED_FRAME_COUNT }, (_, frame) =>
    topIconName.replace(/-top$/, frame < 10 ? `-0${frame}` : `-${frame}`),
  ).filter((name) => name in SPRITE_CELLS);

let sheetBlob: Promise<Blob> | null = null;

const loadSheetBlob = (): Promise<Blob> => {
  if (!sheetBlob) {
    sheetBlob = fetch(spriteUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to load boat sprite sheet ${spriteUrl}: ${response.status}`,
          );
        }
        return response.blob();
      })
      .catch((error) => {
        // Let the next call retry instead of caching the failure
        sheetBlob = null;
        throw error;
      });
  }
  return sheetBlob;
};

// Decode the sheet (off the main thread) and cut the named cells out of it
const sliceSheet = async (names: string[]): Promise<ImageBitmap[]> => {
  const sheet = await createImageBitmap(await loadSheetBlob());
  try {
    return await Promise.all(
      names.map((name) => {
        const { x, y, width, height } = SPRITE_CELLS[name];
        return createImageBitmap(sheet, x, y, width, height);
      }),
    );
  } finally {
    sheet.close();
  }
};

let topIconBitmaps: Promise<{ [name: string]: ImageBitmap }> | null = null;

// The top views are kept so a base map switch, which drops runtime images,
// only has to call addImage again.
const loadTopIconBitmaps = async (): Promise<{
  [name: string]: ImageBitmap;
}> => {
  const bitmaps = await sliceSheet(BOAT_TOP_ICON_NAMES);
  const byName: { [name: string]: ImageBitmap } = {};
  BOAT_TOP_ICON_NAMES.forEach((name, index) => {
    byName[name] = bitmaps[index];
  });
  return byName;
};

/** Add any missing `boat-*-top` images to the map. Safe to call repeatedly. */
export const ensureBoatTopImages = async (
  map: MapWithImages,
): Promise<void> => {
  if (BOAT_TOP_ICON_NAMES.every((name) => map.hasImage(name))) return;

  if (!topIconBitmaps) {
    topIconBitmaps = loadTopIconBitmaps().catch((error) => {
      topIconBitmaps = null;
      throw error;
    });
  }
  const bitmaps = await topIconBitmaps;

  BOAT_TOP_ICON_NAMES.forEach((name) => {
    // Another layer may have added it while the sheet was loading
    if (!map.hasImage(name)) {
      map.addImage(name, bitmaps[name], {
        pixelRatio: SPRITE_CELLS[name].pixelRatio,
      });
    }
  });
};

let tiltedImageQueue: Promise<void> = Promise.resolve();

/**
 * Add the 16 tilted frames for each given top view. Only the types on screen
 * are loaded: addImage copies every frame on the main thread, and all 240
 * frames are about 17 MB of texture.
 */
export const ensureBoatTiltedImages = (
  map: MapWithImages,
  topIconNames: string[],
): Promise<void> => {
  const load = async () => {
    const missing: string[] = [];
    topIconNames.forEach((topIconName) => {
      tiltedBoatIconNames(topIconName).forEach((name) => {
        if (!map.hasImage(name) && missing.indexOf(name) === -1) {
          missing.push(name);
        }
      });
    });
    if (!missing.length) return;

    const bitmaps = await sliceSheet(missing);
    missing.forEach((name, index) => {
      if (!map.hasImage(name)) {
        map.addImage(name, bitmaps[index], {
          pixelRatio: SPRITE_CELLS[name].pixelRatio,
        });
      }
      // addImage copies the pixels, so the bitmap can go
      bitmaps[index].close();
    });
  };
  // One load at a time, so overlapping callers do not decode the sheet twice
  const loaded = tiltedImageQueue.then(load);
  tiltedImageQueue = loaded.catch(() => undefined);
  return loaded;
};
